// Authentication utilities for token management and session handling

/**
 * Check if token is about to expire
 * @param {string} expiresIn - Token expiration time (e.g., '24h', '14d')
 * @returns {boolean} - True if token should be refreshed
 */
export const shouldRefreshToken = (expiresIn) => {
  if (!expiresIn) return false;
  
  // Parse expiration time
  const match = expiresIn.match(/^(\d+)([hd])$/);
  if (!match) return false;
  
  const value = parseInt(match[1]);
  const unit = match[2];
  
  // Convert to hours
  const hours = unit === 'd' ? value * 24 : value;
  
  // If token expires in less than 1 hour, it should be refreshed
  // For extended sessions (14d), refresh when 1 day remains
  const refreshThreshold = hours >= 24 * 7 ? 24 : 1;
  
  return hours <= refreshThreshold;
};

/**
 * Get token expiration time in milliseconds
 * @param {string} expiresIn - Token expiration time (e.g., '24h', '14d')
 * @returns {number} - Expiration time in milliseconds
 */
export const getTokenExpirationTime = (expiresIn) => {
  if (!expiresIn) return 0;
  
  const match = expiresIn.match(/^(\d+)([hd])$/);
  if (!match) return 0;
  
  const value = parseInt(match[1]);
  const unit = match[2];
  
  // Convert to milliseconds
  const ms = unit === 'd' ? value * 24 * 60 * 60 * 1000 : value * 60 * 60 * 1000;
  
  return ms;
};

/**
 * Check if user has active session with remember me
 * @returns {boolean} - True if user has extended session
 */
export const hasExtendedSession = () => {
  const rememberMe = localStorage.getItem('rememberMe');
  const token = localStorage.getItem('token');
  
  return rememberMe === 'true' && !!token;
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('tokenExpiresIn');
};

/**
 * Store authentication data
 * @param {Object} authData - Authentication data
 * @param {string} authData.token - JWT token
 * @param {string} authData.expiresIn - Token expiration time
 * @param {boolean} authData.rememberMe - Remember me flag
 */
export const storeAuthData = ({ token, expiresIn, rememberMe }) => {
  localStorage.setItem('token', token);
  localStorage.setItem('tokenExpiresIn', expiresIn);
  localStorage.setItem('rememberMe', rememberMe.toString());
};

/**
 * Get stored authentication data
 * @returns {Object} - Authentication data
 */
export const getAuthData = () => {
  return {
    token: localStorage.getItem('token'),
    expiresIn: localStorage.getItem('tokenExpiresIn'),
    rememberMe: localStorage.getItem('rememberMe') === 'true'
  };
};